import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Deal, Label } from "@ifgengineering/client-invest-sdk";
import noLogoImage from "@assets/cur8/img/no-logo-deal.png";
import FundraisingCapacityCard from "@components/FundraisingCapacityCard";
import {
  dealUsesFundraisingRounds,
  getDealCapacitySelector,
} from "@state/dealCapacity";
import { RootState } from "@state/rootReducer";
import {
  CardContainer,
  CardContent,
  AssetClassRow,
  AssetClassDot,
  AssetClassText,
  TitleRow,
  DealName,
  LabelsRow,
  LabelGroup,
  LabelCaption,
  LabelCaptionText,
  LabelValueBold,
  LabelValueMedium,
  Divider,
  ImageWrapper,
  CountdownWrapper,
  CountdownOverlay,
  CountdownOpeningText,
  CountdownRow,
  CountdownUnit,
  CountdownNumber,
  CountdownLabel,
} from "./DealCardV2.styled";

interface DealCardV2Props {
  deal: Deal;
  filteredLabels: Label[];
  isPreLaunch?: boolean;
}

// Labels whose name matches these render as primary (bold values)
const PRIMARY_LABEL_NAMES = new Set([
  "Annualised Target Return",
  "Annual Target Yield",
  "Liquidity",
]);

const isPrimaryLabel = (label: Label): boolean =>
  PRIMARY_LABEL_NAMES.has(label.name);

const getTimeRemaining = (targetDate: string) => {
  const diff = new Date(targetDate).getTime() - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, mins: 0, secs: 0 };
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    mins: Math.floor((diff / (1000 * 60)) % 60),
    secs: Math.floor((diff / 1000) % 60),
  };
};

const formatOpeningDate = (date: string) => {
  return new Date(date).toLocaleDateString("en-GB", {
    month: "long",
    day: "numeric",
  });
};

const DealCardV2: FC<DealCardV2Props> = ({
  deal,
  filteredLabels,
  isPreLaunch,
}) => {
  const assetClassName =
    deal.investable?.metadata?.assetClass || "Investment";

  const capacity = useSelector((state: RootState) =>
    getDealCapacitySelector(state, deal.id)
  );

  const labelsWithValues = filteredLabels.filter((l) => l.value);
  const primaryLabels = labelsWithValues.filter(isPrimaryLabel);
  const secondaryLabels = labelsWithValues.filter((l) => !isPrimaryLabel(l));

  const resolvedImageUrl =
    deal.dealImage?.url ||
    deal.investable?.backgroundImage?.url ||
    deal.investable?.displayImage?.url ||
    null;
  const [usingFallback, setUsingFallback] = useState(!resolvedImageUrl);
  const imageUrl = usingFallback ? noLogoImage : resolvedImageUrl!;

  const [countdown, setCountdown] = useState(
    deal.launchDate ? getTimeRemaining(deal.launchDate) : null
  );

  useEffect(() => {
    if (!isPreLaunch || !deal.launchDate) return;
    const interval = setInterval(() => {
      setCountdown(getTimeRemaining(deal.launchDate!));
    }, 1000);
    return () => clearInterval(interval);
  }, [isPreLaunch, deal.launchDate]);

  return (
    <CardContainer>
      <CardContent>
        <AssetClassRow>
          <AssetClassDot />
          <AssetClassText>
            {assetClassName}
            {isPreLaunch ? " — Coming Soon" : ""}
          </AssetClassText>
        </AssetClassRow>

        <TitleRow>
          <DealName>{deal.name}</DealName>
        </TitleRow>

        {labelsWithValues.length > 0 && (
          <LabelsRow>
            {primaryLabels.map((label) => (
              <LabelGroup key={label.id}>
                <LabelCaption>
                  <LabelCaptionText>{label.name}</LabelCaptionText>
                </LabelCaption>
                <LabelValueBold>{label.value}</LabelValueBold>
              </LabelGroup>
            ))}

            {secondaryLabels.length > 0 && primaryLabels.length > 0 && (
              <Divider />
            )}

            {secondaryLabels.map((label) => (
              <LabelGroup key={label.id}>
                <LabelCaption>
                  <LabelCaptionText>{label.name}</LabelCaptionText>
                </LabelCaption>
                <LabelValueMedium>{label.value}</LabelValueMedium>
              </LabelGroup>
            ))}
          </LabelsRow>
        )}

        {dealUsesFundraisingRounds(deal) && capacity ? (
          <FundraisingCapacityCard
            embedded
            capacity={capacity}
            currency={deal.investable?.currency ?? "GBP"}
          />
        ) : null}
      </CardContent>

      {isPreLaunch && deal.launchDate && countdown ? (
        <CountdownWrapper>
          <img
            src={imageUrl}
            alt=""
            style={{
              position: "absolute",
              width: usingFallback ? "60%" : "100%",
              height: usingFallback ? "60%" : "100%",
              top: usingFallback ? "50%" : 0,
              left: usingFallback ? "50%" : 0,
              transform: usingFallback ? "translate(-50%, -50%)" : undefined,
              objectFit: usingFallback ? "contain" : "cover",
            }}
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              setUsingFallback(true);
            }}
          />
          <CountdownOverlay>
            <CountdownOpeningText>
              Opening {formatOpeningDate(deal.launchDate)}
            </CountdownOpeningText>
            <CountdownRow>
              <CountdownUnit>
                <CountdownNumber>{countdown.days}</CountdownNumber>
                <CountdownLabel>Days</CountdownLabel>
              </CountdownUnit>
              <CountdownUnit>
                <CountdownNumber>{countdown.hours}</CountdownNumber>
                <CountdownLabel>Hrs</CountdownLabel>
              </CountdownUnit>
              <CountdownUnit>
                <CountdownNumber>{countdown.mins}</CountdownNumber>
                <CountdownLabel>Mins</CountdownLabel>
              </CountdownUnit>
              <CountdownUnit>
                <CountdownNumber>{countdown.secs}</CountdownNumber>
                <CountdownLabel>Secs</CountdownLabel>
              </CountdownUnit>
            </CountdownRow>
          </CountdownOverlay>
        </CountdownWrapper>
      ) : (
        <ImageWrapper>
          <img
            src={imageUrl}
            alt={deal.name}
            style={{
              width: "100%",
              height: "100%",
              objectFit: usingFallback ? "contain" : "cover",
              padding: usingFallback ? "20%" : 0,
              display: "block",
            }}
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              setUsingFallback(true);
            }}
          />
        </ImageWrapper>
      )}
    </CardContainer>
  );
};

export default DealCardV2;
